import React, { useEffect, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import { useFlashHotelCatalog, useRewardsPool, useUpsertPoolCap } from '~/hooks/apis/rewards';

import { flashConfigState } from './validateFlashConfig';

interface Props {
  /** Salesforce external offer ID the reward pool belongs to. */
  offerId: string;
}

/**
 * Admin card for one offer's Société flash-hotel reward pool: shows the current cap (or
 * "Default" when the offer has no explicit cap and inherits the catalog default), the
 * reserved/redeemed/remaining counts, and the pool's optimistic-concurrency version.
 *
 * Edit mode offers an integer cap field with Save (PUTs the value), and — only once a cap is
 * actually set, since there's nothing to clear otherwise — "Reset to default" (PUTs `null`).
 * A failed save surfaces the server's error message in an inline Alert and leaves the displayed
 * pool untouched: `useUpsertPoolCap` only writes the cache from a successful response, so a
 * rejected mutation never changes what's on screen.
 */
export default function PoolCard({ offerId }: Props) {
  const { data: pool, isLoading, isError } = useRewardsPool(offerId);
  const upsertCap = useUpsertPoolCap(offerId);

  // The "Default" cap label shows the catalog's configured default allocation. There's no local
  // default: while the config is loading the label is the bare word, and if it fails an error says so.
  const configState = flashConfigState(useFlashHotelCatalog());
  const defaultAllocation = configState.status === 'ready' ? configState.config.defaultAllocation : null;

  const [editing, setEditing] = useState(false);
  const [capInput, setCapInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Seed the edit field from the current pool value whenever edit mode is (re)entered. When the
  // offer has no explicit cap, prefill with the catalog's default allocation instead of leaving
  // the field blank — the admin is editing "the cap", and the default is the cap in effect until
  // they save. Only fall back to an empty field when even the default isn't available yet.
  useEffect(() => {
    if (editing) {
      if (pool?.totalQuantity != null) {
        setCapInput(String(pool.totalQuantity));
      } else if (defaultAllocation !== null) {
        setCapInput(String(defaultAllocation));
      } else {
        setCapInput('');
      }
    }
  }, [editing, pool?.totalQuantity, defaultAllocation]);

  const save = async (totalQuantity: number | null) => {
    setErrorMessage(null);
    try {
      await upsertCap.mutateAsync({ totalQuantity });
      setEditing(false);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update cap');
    }
  };

  const handleSave = () => {
    const parsed = Number.parseInt(capInput, 10);
    if (!Number.isInteger(parsed) || parsed < 0 || String(parsed) !== capInput.trim()) {
      setErrorMessage('Enter a whole number of 0 or more');
      return;
    }
    void save(parsed);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isError || !pool) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">Failed to load the reward pool.</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant="h6">Reward Pool</Typography>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {configState.status === 'error' && pool.totalQuantity === null && (
            <Alert severity="error">{configState.message} The default allocation can&apos;t be shown.</Alert>
          )}

          {editing ? (
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <TextField
                label="Cap"
                type="number"
                size="small"
                value={capInput}
                onChange={(e) => setCapInput(e.target.value)}
                slotProps={{ htmlInput: { min: 0, step: 1 } }}
              />
              <LoadingButton variant="contained" loading={upsertCap.isPending} onClick={handleSave}>
                Save
              </LoadingButton>
              {pool.totalQuantity !== null && (
                <LoadingButton variant="text" loading={upsertCap.isPending} onClick={() => save(null)}>
                  Reset to default
                </LoadingButton>
              )}
              <Button
                variant="text"
                disabled={upsertCap.isPending}
                onClick={() => {
                  setEditing(false);
                  setErrorMessage(null);
                }}
              >
                Cancel
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
              <Typography variant="body2">
                Cap:{' '}
                {pool.totalQuantity === null
                  ? defaultAllocation === null
                    ? 'Default'
                    : `Default (${defaultAllocation})`
                  : pool.totalQuantity}
              </Typography>
              <Typography variant="body2">Reserved: {pool.reservedCount}</Typography>
              <Typography variant="body2">Redeemed: {pool.redeemedCount}</Typography>
              <Typography variant="body2">Remaining: {pool.remaining}</Typography>
              <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
                Edit
              </Button>
            </Stack>
          )}

          <Typography variant="caption" color="text.secondary">
            Version: {pool.version}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
